import { Injectable, OnInit } from '@angular/core';
import { AnalyticsRegistryService } from './analytics-registry.service';
import { getEventRouting } from '../constants/analytics-routing.constants';
import { ANALYTICS_EVENTS } from '../constants/event-names.constants';
import { 
  isProviderEnabled, 
  getEnabledProviders, 
  ProviderControl
} from '../config/analytics-provider.config';

@Injectable({
  providedIn: 'root'
})
export class AnalyticsService implements OnInit {
  private isInitialized = false;

  constructor(private registry: AnalyticsRegistryService) {}

  ngOnInit(): void {
    this.initialize();
  }

  /**
   * Initialize the analytics service
   */
  async initialize(): Promise<void> {
    if (this.isInitialized) {
      return;
    }

    try {
      console.log('[AnalyticsService] Initializing analytics service...');
      
      // Check if any providers are enabled based on central config
      const enabledProviders = getEnabledProviders();
      const hasEnabledProviders = enabledProviders.length > 0;
      
      if (!hasEnabledProviders) {
        console.warn('[AnalyticsService] No analytics providers enabled via central config, service will run in disabled mode');
        this.isInitialized = true;
        return;
      }
      
      console.log(`[AnalyticsService] Enabled providers: ${enabledProviders.join(', ')}`);
      
      // Initialize only enabled providers
      await this.registry.initializeProviders();
      
      this.isInitialized = true;
      console.log('[AnalyticsService] Analytics service initialized successfully');
    } catch (error) {
      console.error('[AnalyticsService] Error initializing analytics service:', error);
      
      // Even if initialization fails, mark as initialized to prevent infinite retry loops
      // The service will run in a degraded mode
      this.isInitialized = true;
      
      // Don't throw the error - let the app continue without analytics
      console.warn('[AnalyticsService] Analytics service running in degraded mode due to initialization errors');
    }
  }

  /**
   * Track an event based on automatic routing configuration
   * @param eventName - Name of the event
   * @param properties - Event properties
   */
  async trackEvent(eventName: string, properties?: Record<string, any>): Promise<void> {
    if (!this.isInitialized) {
      console.warn('[AnalyticsService] Analytics service not initialized');
      return;
    }

    console.log(`[AnalyticsService] 🔍 Tracking event: "${eventName}" with properties:`, properties);
    
    const routing = getEventRouting(eventName);
    console.log(`[AnalyticsService] 📍 Event routing found:`, routing);
    
    const targetProviders = routing.providers.filter(providerName => isProviderEnabled(providerName));
    console.log(`[AnalyticsService] ✅ Enabled providers for this event:`, targetProviders);
    
    if (targetProviders.length === 0) {
      console.log(`[AnalyticsService] ⚠️ No enabled providers for event "${eventName}", skipping`);
      return;
    }
    
    console.log(`[AnalyticsService] 🚀 Routing event "${eventName}" to enabled providers: ${targetProviders.join(', ')}`);
    
    const providers = this.registry.getProvidersByNames(targetProviders);
    console.log(`[AnalyticsService] 🔧 Found ${providers.length} provider instances:`, providers.map(p => p.name));
    
    for (const provider of providers) {
      console.log(`[AnalyticsService] 📤 Sending to provider: ${provider.name}`);
      console.log(`[AnalyticsService] 📊 Provider status - enabled: ${provider.isEnabled}, initialized: ${provider.isInitialized}`);
      
      if (provider.isEnabled && provider.isInitialized) {
        try {
          const startTime = Date.now();
          await provider.trackEvent(eventName, properties);
          const responseTime = Date.now() - startTime;
          
          this.registry.updateProviderStats(provider.name, 'success', responseTime);
          console.log(`[AnalyticsService] ✅ Event "${eventName}" sent to ${provider.name} successfully`);
        } catch (error) {
          this.registry.updateProviderStats(provider.name, 'error');
          console.error(`[AnalyticsService] ❌ Error tracking event in ${provider.name}:`, error);
        }
      } else {
        console.warn(`[AnalyticsService] ⚠️ Provider ${provider.name} not ready - enabled: ${provider.isEnabled}, initialized: ${provider.isInitialized}`);
      }
    }
  }

  /**
   * Track an event to all enabled analytics providers (override routing)
   * @param eventName - Name of the event
   * @param properties - Event properties
   */
  async trackEventToAllProviders(eventName: string, properties?: Record<string, any>): Promise<void> {
    if (!this.isInitialized) {
      console.warn('[AnalyticsService] Analytics service not initialized');
      return;
    }

    const enabledProviders = getEnabledProviders();
    if (enabledProviders.length === 0) {
      console.log('[AnalyticsService] No providers enabled, skipping event tracking');
      return;
    }

    console.log(`[AnalyticsService] Override routing - sending event "${eventName}" to enabled providers: ${enabledProviders.join(', ')}`);
    
    const providers = this.registry.getProvidersByNames(enabledProviders);
    
    for (const provider of providers) {
      if (provider.isEnabled && provider.isInitialized) {
        try {
          const startTime = Date.now();
          await provider.trackEvent(eventName, properties);
          const responseTime = Date.now() - startTime;
          
          this.registry.updateProviderStats(provider.name, 'success', responseTime);
          console.log(`[AnalyticsService] Event "${eventName}" sent to ${provider.name}`);
        } catch (error) {
          this.registry.updateProviderStats(provider.name, 'error');
          console.error(`Error tracking event in ${provider.name}:`, error);
        }
      }
    }
  }

  /**
   * Track an event to specific providers only (override routing)
   * @param eventName - Name of the event
   * @param properties - Event properties
   * @param providerNames - Array of provider names to send the event to
   */
  async trackEventToProviders(eventName: string, properties: Record<string, any> | undefined, providerNames: string[]): Promise<void> {
    if (!this.isInitialized) {
      console.warn('[AnalyticsService] Analytics service not initialized');
      return;
    }

    // Filter to only enabled providers
    const enabledProviderNames = providerNames.filter(name => isProviderEnabled(name));
    
    if (enabledProviderNames.length === 0) {
      console.log(`[AnalyticsService] No enabled providers in requested list: ${providerNames.join(', ')}, skipping event`);
      return;
    }

    console.log(`[AnalyticsService] Override routing - sending event "${eventName}" to enabled providers: ${enabledProviderNames.join(', ')}`);
    
    const providers = this.registry.getProvidersByNames(enabledProviderNames);
    
    for (const provider of providers) {
      if (provider.isEnabled && provider.isInitialized) {
        try {
          const startTime = Date.now();
          await provider.trackEvent(eventName, properties);
          const responseTime = Date.now() - startTime;
          
          this.registry.updateProviderStats(provider.name, 'success', responseTime);
          console.log(`[AnalyticsService] Event "${eventName}" sent to ${provider.name}`);
        } catch (error) {
          this.registry.updateProviderStats(provider.name, 'error');
          console.error(`Error tracking event in ${provider.name}:`, error);
        }
      }
    }
  }

  /**
   * Identify user based on automatic routing configuration
   * @param userId - User identifier
   * @param properties - User properties
   */
  async identifyUser(userId: string, properties?: Record<string, any>): Promise<void> {
    if (!this.isInitialized) {
      console.warn('[AnalyticsService] Analytics service not initialized');
      return;
    }

    if (!userId || typeof userId !== 'string') {
      console.error('[AnalyticsService] Invalid userId provided for identifyUser:', userId);
      return;
    }

    console.log(`[AnalyticsService] 🔍 Identifying user: "${userId}" with properties:`, properties);
    
    // Try to get routing for user_identified event
    let routing = getEventRouting('user_identified');
    let targetProviders = routing.providers.filter(providerName => isProviderEnabled(providerName));
    
    // If no routing found or no providers enabled, fallback to all enabled providers
    if (targetProviders.length === 0) {
      console.log('[AnalyticsService] No routing found for user_identified, using all enabled providers');
      targetProviders = getEnabledProviders();
    }
    
    if (targetProviders.length === 0) {
      console.log('[AnalyticsService] No enabled providers for user identification, skipping');
      return;
    }
    
    console.log(`[AnalyticsService] 🚀 Routing user identification to providers: ${targetProviders.join(', ')}`);
    
    const providers = this.registry.getProvidersByNames(targetProviders);
    console.log(`[AnalyticsService] 🔧 Found ${providers.length} provider instances:`, providers.map(p => p.name));
    
    for (const provider of providers) {
      console.log(`[AnalyticsService] 📤 Sending user identification to provider: ${provider.name}`);
      console.log(`[AnalyticsService] 📊 Provider status - enabled: ${provider.isEnabled}, initialized: ${provider.isInitialized}`);
      
      if (provider.isEnabled && provider.isInitialized) {
        try {
          const startTime = Date.now();
          await provider.identifyUser(userId, properties);
          const responseTime = Date.now() - startTime;
          
          this.registry.updateProviderStats(provider.name, 'success', responseTime);
          console.log(`[AnalyticsService] ✅ User identification sent to ${provider.name} successfully`);
        } catch (error) {
          this.registry.updateProviderStats(provider.name, 'error');
          console.error(`[AnalyticsService] ❌ Error identifying user in ${provider.name}:`, error);
        }
      } else {
        console.warn(`[AnalyticsService] ⚠️ Provider ${provider.name} not ready - enabled: ${provider.isEnabled}, initialized: ${provider.isInitialized}`);
      }
    }
  }

  /**
   * Set user properties based on automatic routing configuration
   * @param properties - User properties to set
   */
  async setUserProperties(properties: Record<string, any>): Promise<void> {
    if (!this.isInitialized) {
      console.warn('[AnalyticsService] Analytics service not initialized');
      return;
    }

    if (!properties || typeof properties !== 'object') {
      console.error('[AnalyticsService] Invalid properties provided for setUserProperties:', properties);
      return;
    }

    console.log(`[AnalyticsService] 🔍 Setting user properties:`, properties);
    
    // Try to get routing for user_properties_set event
    let routing = getEventRouting('user_properties_set');
    let targetProviders = routing.providers.filter(providerName => isProviderEnabled(providerName));
    
    // If no routing found or no providers enabled, fallback to all enabled providers
    if (targetProviders.length === 0) {
      console.log('[AnalyticsService] No routing found for user_properties_set, using all enabled providers');
      targetProviders = getEnabledProviders();
    }
    
    if (targetProviders.length === 0) {
      console.log('[AnalyticsService] No enabled providers for user properties, skipping');
      return;
    }
    
    console.log(`[AnalyticsService] 🚀 Routing user properties to providers: ${targetProviders.join(', ')}`);
    
    const providers = this.registry.getProvidersByNames(targetProviders);
    console.log(`[AnalyticsService] 🔧 Found ${providers.length} provider instances:`, providers.map(p => p.name));
    
    for (const provider of providers) {
      console.log(`[AnalyticsService] 📤 Sending user properties to provider: ${provider.name}`);
      console.log(`[AnalyticsService] 📊 Provider status - enabled: ${provider.isEnabled}, initialized: ${provider.isInitialized}`);
      
      if (provider.isEnabled && provider.isInitialized) {
        try {
          const startTime = Date.now();
          await provider.setUserProperties(properties);
          const responseTime = Date.now() - startTime;
          
          this.registry.updateProviderStats(provider.name, 'success', responseTime);
          console.log(`[AnalyticsService] ✅ User properties sent to ${provider.name} successfully`);
        } catch (error) {
          this.registry.updateProviderStats(provider.name, 'error');
          console.error(`[AnalyticsService] ❌ Error setting user properties in ${provider.name}:`, error);
        }
      } else {
        console.warn(`[AnalyticsService] ⚠️ Provider ${provider.name} not ready - enabled: ${provider.isEnabled}, initialized: ${provider.isInitialized}`);
      }
    }
  }

  /**
   * Track page view based on automatic routing configuration
   * @param properties - Page view properties
   */
  async trackPageView(properties?: Record<string, any>): Promise<void> {
    if (!this.isInitialized) {
      console.warn('[AnalyticsService] Analytics service not initialized');
      return;
    }

    const routing = getEventRouting('page_view');
    const targetProviders = routing.providers.filter(providerName => isProviderEnabled(providerName));
    
    if (targetProviders.length === 0) {
      console.log('[AnalyticsService] No enabled providers for page view, skipping');
      return;
    }
    
    console.log(`[AnalyticsService] Routing page view to enabled providers: ${targetProviders.join(', ')}`);
    
    const providers = this.registry.getProvidersByNames(targetProviders);
    
    for (const provider of providers) {
      if (provider.isEnabled && provider.isInitialized) {
        try {
          const startTime = Date.now();
          await provider.trackPageView(properties);
          const responseTime = Date.now() - startTime;
          
          this.registry.updateProviderStats(provider.name, 'success', responseTime);
          console.log(`[AnalyticsService] Page view sent to ${provider.name}`);
        } catch (error) {
          this.registry.updateProviderStats(provider.name, 'error');
          console.error(`Error tracking page view in ${provider.name}:`, error);
        }
      }
    }
  }

  /**
   * Get provider status information
   */
  getProvidersStatus() {
    const registryStatus = this.registry.getProvidersStatus();
    const centralConfig = this.getCentralProviderStatus();
    
    return {
      registry: registryStatus,
      centralConfig: centralConfig,
      overall: {
        enabledProviders: getEnabledProviders(),
        totalProviders: Object.keys(centralConfig).length
      }
    };
  }

  /**
   * Get central provider configuration status
   */
  private getCentralProviderStatus() {
    return ProviderControl.getProviderStatus();
  }

  /**
   * Debug method to check current analytics status
   */
  debugAnalyticsStatus(): void {
    console.log('🔍 === ANALYTICS DEBUG STATUS ===');
    console.log('📊 Service initialized:', this.isInitialized);
    console.log('📋 Central config:', this.getCentralProviderStatus());
    console.log('🔧 Registry status:', this.registry.getProvidersStatus());
    console.log('📍 Enabled providers:', getEnabledProviders());
    console.log('🎯 Event routing for user_profile_update:', getEventRouting('user_profile_update'));
    console.log('================================');
  }

  /**
   * Debug method specifically for auth events
   */
  debugAuthEvent(eventName: string): void {
    console.log('🔐 === AUTH EVENT DEBUG ===');
    console.log('📝 Event name:', eventName);
    console.log('🎯 Event routing:', getEventRouting(eventName));
    console.log('✅ PostHog enabled:', isProviderEnabled('posthog'));
    console.log('✅ MoEngage enabled:', isProviderEnabled('moengage'));
    console.log('🔧 Service initialized:', this.isInitialized);
    console.log('📊 Registry providers:', this.registry.getAllProviders().map(p => ({ name: p.name, enabled: p.isEnabled, initialized: p.isInitialized })));
    console.log('================================');
  }

  /**
   * Destroy session for specific provider (e.g., MoEngage)
   * Useful when users log out or when you need to reset session state
   */
  async destroySession(providerName: string): Promise<void> {
    if (!this.isInitialized) {
      console.warn('[AnalyticsService] Analytics service not initialized');
      return;
    }

    if (!isProviderEnabled(providerName)) {
      console.log(`[AnalyticsService] Provider '${providerName}' is not enabled, skipping session destruction`);
      return;
    }

    try {
      const providers = this.registry.getProvidersByNames([providerName]);
      
      if (providers.length === 0) {
        console.warn(`[AnalyticsService] Provider '${providerName}' not found`);
        return;
      }

      const provider = providers[0];
      
      if (provider.isEnabled && provider.isInitialized) {
        // Check if the provider has a destroySession method
        if (typeof (provider as any).destroySession === 'function') {
          await (provider as any).destroySession();
          console.log(`[AnalyticsService] Session destroyed for provider: ${providerName}`);
        } else {
          console.warn(`[AnalyticsService] Provider '${providerName}' does not support session destruction`);
        }
      } else {
        console.warn(`[AnalyticsService] Provider '${providerName}' not ready - enabled: ${provider.isEnabled}, initialized: ${provider.isInitialized}`);
      }
    } catch (error) {
      console.error(`[AnalyticsService] Error destroying session for provider '${providerName}':`, error);
      throw error;
    }
  }

  /**
   * Destroy sessions for all enabled providers
   */
  async destroyAllSessions(): Promise<void> {
    if (!this.isInitialized) {
      console.warn('[AnalyticsService] Analytics service not initialized');
      return;
    }

    const enabledProviders = getEnabledProviders();
    
    if (enabledProviders.length === 0) {
      console.log('[AnalyticsService] No providers enabled, skipping session destruction');
      return;
    }

    console.log(`[AnalyticsService] Destroying sessions for enabled providers: ${enabledProviders.join(', ')}`);
    
    for (const providerName of enabledProviders) {
      try {
        await this.destroySession(providerName);
      } catch (error) {
        console.error(`[AnalyticsService] Error destroying session for provider '${providerName}':`, error);
        // Continue with other providers even if one fails
      }
    }
  }

  /**
   * Set MoEngage user attributes (first name, last name, email, mobile)
   * @param attributes - User attributes to set
   */
  setMoEngageUserAttributes(attributes: {
    firstName?: string;
    lastName?: string;
    email?: string;
    mobile?: string;
  }): void {
    if (!this.isInitialized) {
      console.warn('[AnalyticsService] Analytics service not initialized');
      return;
    }

    if (!isProviderEnabled('moengage')) {
      console.log('[AnalyticsService] MoEngage provider is not enabled, skipping user attributes');
      return;
    }

    try {
      const providers = this.registry.getProvidersByNames(['moengage']);
      
      if (providers.length === 0) {
        console.warn('[AnalyticsService] MoEngage provider not found');
        return;
      }

      const moengageProvider = providers[0];
      
      if (moengageProvider.isEnabled && moengageProvider.isInitialized) {
        // Check if the provider has the setUserAttributes method
        if (typeof (moengageProvider as any).setUserAttributes === 'function') {
          (moengageProvider as any).setUserAttributes(attributes);
          console.log('[AnalyticsService] ✅ MoEngage user attributes set successfully:', attributes);
        } else {
          console.warn('[AnalyticsService] MoEngage provider does not support setUserAttributes method');
        }
      } else {
        console.warn('[AnalyticsService] MoEngage provider not ready - enabled: ${moengageProvider.isEnabled}, initialized: ${moengageProvider.isInitialized}');
      }
    } catch (error) {
      console.error('[AnalyticsService] Error setting MoEngage user attributes:', error);
    }
  }

  /**
   * Set individual MoEngage user attributes
   */
  setMoEngageFirstName(firstName: string): void {
    this.setMoEngageUserAttributes({ firstName });
  }

  setMoEngageLastName(lastName: string): void {
    this.setMoEngageUserAttributes({ lastName });
  }

  setMoEngageEmail(email: string): void {
    this.setMoEngageUserAttributes({ email });
  }

  setMoEngageMobile(mobile: string): void {
    this.setMoEngageUserAttributes({ mobile });
  }
} 